import { DEFAULT_ECOPACK_IMAGE_URL } from '@/constants';
import type { Product } from '@/pages/store/store-detail.types';

interface StoreProductListProps {
  products: Product[];
  hidingProducts: Set<string>;
  readonly?: boolean;
  onProductClick: (productId: string) => void;
}

interface ProductCardProps {
  product: Product;
  isHiding: boolean;
  readonly: boolean;
  onClick: (productId: string) => void;
}

function ProductCard({ product, isHiding, readonly, onClick }: ProductCardProps) {
  const isOutOfStock = product.discount_type !== 'RESERVATION_DISCOUNT' && (product.stock_quantity === 0 || !product.is_available);
  const isDeadlineInactive = product.discount_type === 'DEADLINE_DISCOUNT' && product.deadlineOfferAvailableToday === false;
  const isDisabled = isOutOfStock || isDeadlineInactive;
  const discountRateFromPrice = product.original_price > 0
    ? Math.round(((product.original_price - product.discounted_price) / product.original_price) * 100)
    : 0;
  const baseDiscountRate = product.discount_rate > 0
    ? product.discount_rate
    : Math.max(discountRateFromPrice, 0);
  const displayDiscountRate = product.discount_type === 'DEADLINE_DISCOUNT'
    ? (baseDiscountRate > 0 ? baseDiscountRate : 40)
    : baseDiscountRate;
  const derivedOriginalPrice = product.discount_type === 'DEADLINE_DISCOUNT'
    ? (() => {
        if (product.original_price > product.discounted_price) return product.original_price;
        const rate = displayDiscountRate || 40;
        const estimated = product.discounted_price / (1 - rate / 100);
        return Math.ceil(estimated / 10) * 10; // 1원 단위 올림하여 10원 단위로 표시
      })()
    : product.original_price;
  const hasDiscount = product.discount_type === 'DEADLINE_DISCOUNT'
    ? true
    : (product.discounted_price < product.original_price && displayDiscountRate > 0);
  const productImageUrl = product.image_url || (product.discount_type === 'DEADLINE_DISCOUNT' ? DEFAULT_ECOPACK_IMAGE_URL : undefined);

  return (
    <div
      onClick={() => {
        if (readonly) return;
        onClick(product.id);
      }}
      className={`
        relative bg-white border-2 border-[#DEDEDE] rounded-lg p-2 mb-4 ${readonly ? 'cursor-default' : 'cursor-pointer'}
        transition-all duration-500 ease-out
        ${isHiding 
          ? 'opacity-0 scale-95 -translate-y-2' 
          : 'opacity-100 scale-100 translate-y-0'
        }
        ${isDisabled
          ? 'grayscale opacity-50' 
          : readonly ? '' : 'hover:shadow-md'
        }
      `}
    >
      {/* 품절 오버레이 - 예약할인 제외 */}
      {product.discount_type !== 'RESERVATION_DISCOUNT' && isOutOfStock && (
        <div className="pointer-events-none absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center z-10">
          <span className="text-white text-xl font-bold">품절</span>
        </div>
      )}
      {/* 마감할인 비활성 오버레이 */}
      {isDeadlineInactive && !isOutOfStock && (
        <div className="pointer-events-none absolute inset-0 bg-black/20 rounded-lg z-10" />
      )}
      
      {/* 제품명 */}
      <h4 className="text-base font-semibold text-[#222222] mb-3">{product.name}</h4>
      
      {/* 설명과 이미지 */}
      <div className="flex gap-6 mb-3">
        <div className="flex-1">
          {product.description && (
            <p className="text-base text-[#222222] leading-normal mb-1">
              {product.description}
            </p>
          )}
        </div>
        {productImageUrl && (
          <div className="w-16 h-16 flex-shrink-0">
            <img
              src={productImageUrl}
              alt={product.name}
              className="w-full h-full object-cover rounded-lg"
            />
          </div>
        )}
      </div>
      
      {/* 재고, 가격 정보 */}
      <div className="flex items-center justify-between">
        {/* 재고 */}
        {product.discount_type === 'RESERVATION_DISCOUNT' ? (
          <div className="flex items-center gap-1 border-b border-[#222222] pb-0.5">
            <span className="text-base text-green-600 font-medium">예약 가능</span>
          </div>
        ) : (
          <div className="flex items-center gap-1 border-b border-[#222222] pb-0.5">
            <span className="text-base text-[#222222]">재고</span>
            <span className={`text-base font-semibold ${
              product.stock_quantity <= 1 ? 'text-[#FF3B30]' : 'text-[#222222]'
            }`}>
              {product.stock_quantity}
            </span>
            <span className="text-base text-[#222222]">개</span>
          </div>
        )}
        
        {/* 가격 정보 */}
        <div className="flex items-center gap-1">
          {/* 원가 */}
          {hasDiscount && (
            <div className="flex items-center gap-1 mr-2">
              <span className="text-sm text-[#222222] line-through">
                {derivedOriginalPrice.toLocaleString()}원
              </span>
              <img 
                src="/icons/icon_arrow_right.svg" 
                alt="할인 화살표" 
                className="w-4 h-4"
              />
            </div>
          )}
          
          {/* 할인율과 할인가 */}
          <div className="flex items-center gap-1">
            {hasDiscount && (
              <span className="text-sm font-medium text-[#FF3B30]">
                (-{displayDiscountRate}%)
              </span>
            )}
            <span className="text-base font-semibold text-[#222222]">
              {product.discounted_price.toLocaleString()}원
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategorySection({ 
  category, 
  products, 
  hidingProducts, 
  readonly, 
  onProductClick 
}: {
  category: string;
  products: Product[];
  hidingProducts: Set<string>;
  readonly: boolean;
  onProductClick: (productId: string) => void;
}) {
  return (
    <div className="mb-6">
      <h3 className="text-xl font-semibold text-[#222222] mb-2">
        {category || '카테고리 없음'}
      </h3>
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          isHiding={hidingProducts.has(product.id)}
          readonly={readonly}
          onClick={onProductClick}
        />
      ))}
    </div>
  );
}

export function StoreProductList({ 
  products, 
  hidingProducts, 
  readonly = false, 
  onProductClick 
}: StoreProductListProps) {
  if (products.length === 0) {
    return (
      <div className="px-4 pb-20">
        <div className="text-center py-12">
          <p className="text-gray-500">선택한 주문 유형에 해당하는 상품이 없습니다</p>
        </div>
      </div>
    );
  }

  // 카테고리별로 상품을 그룹화
  const productsByCategory = products.reduce((acc, product) => {
    const category = product.category || '';
    if (!acc[category]) {
      acc[category] = [];
    }
    acc[category].push(product);
    return acc;
  }, {} as Record<string, Product[]>);

  // 랜덤박스를 제외한 카테고리들을 먼저 정렬
  const otherCategories = Object.keys(productsByCategory).filter(cat => cat !== '랜덤박스').sort();
  const hasRandomBox = '랜덤박스' in productsByCategory;

  return (
    <div className="px-4 pb-20">
      <div className="space-y-4">
        {/* 일반 카테고리들 먼저 표시 */}
        {otherCategories.map((category) => (
          <CategorySection
            key={category || 'uncategorized'}
            category={category}
            products={productsByCategory[category]}
            hidingProducts={hidingProducts}
            readonly={readonly}
            onProductClick={onProductClick}
          />
        ))}

        {/* 랜덤박스 카테고리는 맨 아래에 표시 */}
        {hasRandomBox && (
          <CategorySection
            category="랜덤박스"
            products={productsByCategory['랜덤박스']}
            hidingProducts={hidingProducts}
            readonly={readonly}
            onProductClick={onProductClick}
          />
        )}
      </div>
    </div>
  );
}
